/**
 * RetailerCorporateDashboard
 *
 * Corporate parent landing page (V1.1 Multi-Location).
 * Visible only to users with account_type === 'corporate' OR role_in_team === 'corporate_admin'.
 *
 * Tabs:
 *   - Profile    : edit umbrella brand identity
 *   - Locations  : list / create / edit / archive child locations
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, Plus, Edit3, Archive, RotateCcw, Save, Loader2 } from 'lucide-react';
import RetailerLayout from './RetailerLayout';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { corporate, getErrorMessage } from '../../utils/api';

function ProfileTab() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    corporate.getProfile()
      .then(r => setData(r.data))
      .catch(e => toast.error(getErrorMessage(e, 'Could not load corporate profile')));
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      await corporate.updateProfile({
        name: data.name,
        brand_name: data.brand_name,
        corporate_website: data.corporate_website,
        corporate_description: data.corporate_description,
        primary_contact_email: data.primary_contact_email,
        primary_contact_phone: data.primary_contact_phone,
      });
      toast.success('Corporate profile updated');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;
  }

  return (
    <Card className="border-0 shadow-md">
      <CardContent className="p-6 space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label>Corporate name</Label>
            <Input
              data-testid="corp-name-input"
              value={data.name || ''}
              onChange={e => setData({ ...data, name: e.target.value })}
            />
          </div>
          <div>
            <Label>Brand name (optional)</Label>
            <Input
              data-testid="corp-brand-input"
              value={data.brand_name || ''}
              onChange={e => setData({ ...data, brand_name: e.target.value })}
            />
          </div>
          <div>
            <Label>Primary contact email</Label>
            <Input
              type="email"
              data-testid="corp-email-input"
              value={data.primary_contact_email || ''}
              onChange={e => setData({ ...data, primary_contact_email: e.target.value })}
            />
          </div>
          <div>
            <Label>Primary contact phone</Label>
            <Input
              data-testid="corp-phone-input"
              value={data.primary_contact_phone || ''}
              onChange={e => setData({ ...data, primary_contact_phone: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <Label>Website</Label>
            <Input
              data-testid="corp-website-input"
              placeholder="https://..."
              value={data.corporate_website || ''}
              onChange={e => setData({ ...data, corporate_website: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <Label>Description</Label>
            <Textarea
              data-testid="corp-description-input"
              rows={3}
              value={data.corporate_description || ''}
              onChange={e => setData({ ...data, corporate_description: e.target.value })}
            />
          </div>
        </div>
        <div className="flex items-center justify-between border-t pt-4">
          <div className="text-sm text-gray-500">
            <Building2 className="w-4 h-4 inline mr-1" />
            {data.location_count} active location{data.location_count === 1 ? '' : 's'} under this corporate
          </div>
          <Button data-testid="corp-save-btn" onClick={onSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save changes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function LocationFormModal({ open, onOpenChange, initial, onSaved }) {
  const editing = !!initial?.id;
  const blank = {
    store_name: '', store_address: '', store_address_line2: '',
    store_city: '', store_state: '', store_zip: '',
    contact_email: '', contact_phone: '',
    payout_inherits_from_corporate: true,
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...blank, ...initial } : blank);
  }, [open, initial]);

  const submit = async () => {
    if (!form.store_name || !form.store_address || !form.contact_email) {
      toast.error('Store name, address, and contact email are required');
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await corporate.updateLocation(initial.id, form);
        toast.success('Location updated');
      } else {
        await corporate.createLocation(form);
        toast.success(
          `Location created. A welcome email with sign-in instructions was sent to ${form.contact_email}.`,
          { duration: 8000 }
        );
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="location-form-modal" data-form-type="other">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit location' : 'Add new location'}</DialogTitle>
        </DialogHeader>
        {/* NOT a <form> — Chrome's "Save Address?" heuristic anchors on form
            submission detection. By using a plain <div>, Chrome treats the
            inputs as ad-hoc fields and never offers to save the address. */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Store name *</Label>
            <Input
              data-testid="loc-name-input"
              autoComplete="off"
              value={form.store_name}
              onChange={e => set('store_name', e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <Label>Street address *</Label>
            <Input
              data-testid="loc-address-input"
              autoComplete="dealshaq-loc-street"
              name="dealshaq-loc-street"
              value={form.store_address}
              onChange={e => set('store_address', e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <Label>Address line 2</Label>
            <Input
              autoComplete="dealshaq-loc-street2"
              name="dealshaq-loc-street2"
              value={form.store_address_line2 || ''}
              onChange={e => set('store_address_line2', e.target.value)}
            />
          </div>
          <div>
            <Label>City</Label>
            <Input
              data-testid="loc-city-input"
              autoComplete="dealshaq-loc-city"
              name="dealshaq-loc-city"
              value={form.store_city || ''}
              onChange={e => set('store_city', e.target.value)}
            />
          </div>
          <div>
            <Label>State</Label>
            <Input
              data-testid="loc-state-input"
              autoComplete="dealshaq-loc-state"
              name="dealshaq-loc-state"
              value={form.store_state || ''}
              onChange={e => set('store_state', e.target.value)}
            />
          </div>
          <div>
            <Label>ZIP</Label>
            <Input
              data-testid="loc-zip-input"
              autoComplete="dealshaq-loc-zip"
              name="dealshaq-loc-zip"
              value={form.store_zip || ''}
              onChange={e => set('store_zip', e.target.value)}
            />
          </div>
          <div>
            <Label>Contact phone</Label>
            <Input
              autoComplete="dealshaq-loc-phone"
              name="dealshaq-loc-phone"
              value={form.contact_phone || ''}
              onChange={e => set('contact_phone', e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <Label>Login email * {editing && <span className="text-xs text-gray-500">(changing this also changes the login)</span>}</Label>
            <Input
              type="email"
              data-testid="loc-email-input"
              autoComplete="dealshaq-loc-email"
              name="dealshaq-loc-email"
              value={form.contact_email}
              onChange={e => set('contact_email', e.target.value)}
            />
          </div>
          <div className="md:col-span-2 flex items-center justify-between rounded-lg border p-3 bg-amber-50">
            <div>
              <div className="font-medium text-sm">Inherit payout settings from Corporate</div>
              <div className="text-xs text-gray-600">When on, this location uses the corporate Stripe/bank for payouts.</div>
            </div>
            <Switch
              data-testid="loc-payout-inherit-switch"
              checked={!!form.payout_inherits_from_corporate}
              onCheckedChange={v => set('payout_inherits_from_corporate', v)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button data-testid="loc-save-btn" onClick={submit} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {editing ? 'Save changes' : 'Create location'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LocationsTab() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const reload = () => {
    corporate.listLocations()
      .then(r => setList(r.data.locations || []))
      .catch(e => {
        toast.error(getErrorMessage(e, 'Could not load locations'));
        setList([]);
      });
  };
  useEffect(reload, []);

  const archive = async (loc) => {
    if (!window.confirm(`Archive "${loc.store_name}"? It will stop accepting new orders.`)) return;
    try {
      await corporate.archiveLocation(loc.id);
      toast.success('Location archived');
      reload();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Archive failed'));
    }
  };

  const togglePayoutOverride = async (loc) => {
    try {
      await corporate.togglePayoutOverride(loc.id, !loc.payout_inherits_from_corporate);
      reload();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Toggle failed'));
    }
  };

  if (list === null) {
    return <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />Loading locations…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          data-testid="add-location-btn"
          onClick={() => { setEditing(null); setModalOpen(true); }}
        >
          <Plus className="w-4 h-4 mr-2" />Add location
        </Button>
      </div>

      {list.length === 0 && (
        <Card className="border-dashed border-2 bg-gray-50">
          <CardContent className="p-10 text-center text-gray-500">
            <MapPin className="w-10 h-10 mx-auto mb-3 opacity-60" />
            No locations yet. Add your first storefront to get started.
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {list.map(loc => (
          <Card
            key={loc.id}
            data-testid={`location-card-${loc.id}`}
            className={`border-0 shadow-md ${loc.store_status === 'archived' ? 'opacity-60' : ''}`}
          >
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{loc.store_name}</h3>
                  <p className="text-sm text-gray-600">
                    {[loc.store_address, loc.store_city, loc.store_state].filter(Boolean).join(', ')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{loc.contact_email}</p>
                </div>
                {loc.store_status === 'archived' && (
                  <span className="text-xs font-medium bg-red-100 text-red-800 px-2 py-1 rounded">Archived</span>
                )}
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <button
                  type="button"
                  onClick={() => togglePayoutOverride(loc)}
                  className="text-xs text-gray-600 hover:text-gray-900"
                  data-testid={`loc-payout-toggle-${loc.id}`}
                >
                  Payout: <span className="font-medium">{loc.payout_inherits_from_corporate ? 'inherits corporate' : 'override'}</span>
                </button>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`edit-location-${loc.id}`}
                    onClick={() => { setEditing(loc); setModalOpen(true); }}
                  >
                    <Edit3 className="w-4 h-4" />
                  </Button>
                  {loc.store_status !== 'archived' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`archive-location-${loc.id}`}
                      onClick={() => archive(loc)}
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <LocationFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        initial={editing}
        onSaved={reload}
      />
    </div>
  );
}

export default function RetailerCorporateDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('locations');

  return (
    <RetailerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-6xl mx-auto">
        <div className="bg-gradient-to-r from-slate-900 to-indigo-900 rounded-2xl p-8 text-white shadow-xl flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
              Corporate
            </h1>
            <p className="text-indigo-100">Manage your umbrella brand and all store locations</p>
          </div>
          <Button
            variant="ghost"
            className="text-indigo-100 hover:text-white hover:bg-white/10"
            onClick={() => navigate('/retailer/settings')}
            data-testid="back-to-settings-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />Settings
          </Button>
        </div>

        <div className="flex gap-2 border-b">
          {[
            { key: 'locations', label: 'Locations', icon: MapPin },
            { key: 'profile',   label: 'Profile',   icon: Building2 },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              data-testid={`tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
                tab === key
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>

        {tab === 'locations' ? <LocationsTab /> : <ProfileTab />}
      </div>
    </RetailerLayout>
  );
}
